import { useRecruitmentApi } from 'infrastructure/api';
import { CANDIDATE_RESOURCE_URL } from 'domain/shared/candidate';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { useBaseCrudService } from 'framework/crud';
import { useVacancyGroupService } from 'domain/shared/newVacancy';
import { FORMAT_ENUM_VALUES } from 'domain/shared/newVacancy';

const EXPECTED_COMPENSATION_URL = '/compensations';

const fieldsToEncrypt = ['expectedAmountFrom', 'expectedAmountTo'];

const useExpectedCompensationService = () => {
  const { sendGetRequest } = useRecruitmentApi();
  const { retrieveSensitiveRecruitingInfo } = useVacancyGroupService();
  const parentResourceUrl = `${CANDIDATE_RESOURCE_URL}`;

  const { addMessage } = useMessageService();

  const { getById: initialGetById, ...baseCrudRequests } = useBaseCrudService({
    singleResourceUrl: `${parentResourceUrl}${EXPECTED_COMPENSATION_URL}/:id`,
    listResourceUrl: `${parentResourceUrl}${EXPECTED_COMPENSATION_URL}`,
    useService: useRecruitmentApi,
  });

  const getById = async (compensationId: any) => {
    try {
      const compensationResponse = await initialGetById(compensationId);

      if (!compensationResponse) {
        return null;
      }

      const allIds: string[] = fieldsToEncrypt.map(fieldName => compensationResponse[`${fieldName}Id`]).filter(Boolean);

      let sensitiveInfoMap;
      try {
        sensitiveInfoMap = await retrieveSensitiveRecruitingInfo(allIds);
      } catch (error) {
        sensitiveInfoMap = new Map();
      }

      const updatedCompensationResponse = { ...compensationResponse } as any;

      fieldsToEncrypt.forEach(fieldName => {
        const id = compensationResponse[`${fieldName}Id`];
        updatedCompensationResponse[fieldName] = sensitiveInfoMap.get(id) || '';
      });

      updatedCompensationResponse.location =
        FORMAT_ENUM_VALUES[updatedCompensationResponse.registrationLocation] ??
        updatedCompensationResponse.registrationLocation;

      return updatedCompensationResponse;
    } catch (error) {
      addMessage(new ErrorMessage('Error in getting compensation.'));
      throw error;
    }
  };

  const getCompensationPermissions = async (compensationId: any) => {
    try {
      const response = await sendGetRequest(`/permissions/ExpectedCompensation/${compensationId}`);
      return response.json();
    } catch (error) {
      addMessage(new ErrorMessage('Failed to fetch permissions for expected compensation.'));
      return [];
    }
  };

  return {
    ...baseCrudRequests,
    getById,
    getCompensationPermissions,
  };
};

export default useExpectedCompensationService;
